import { AnimationModeConfig } from './AnimationTypes';
import { DeviceProfile } from '@/components/provider/UserCurrentProvider';
import { IMAGE_SIZES } from '@/utils/imageSizes';
import type { ImageSize as FrameSize } from '@/utils/imageSizes';

// Keep your preferred destructuring syntax intact
const ORDERED_SIZES: FrameSize[] = [...IMAGE_SIZES];

/**
 * Calculates a reduced frame size based on a step index offset.
 */
const downgradeFrameSize = (
    currentSize: FrameSize,
    steps: number,
): FrameSize => {
    const currentIndex = ORDERED_SIZES.indexOf(currentSize);
    if (currentIndex === -1) return currentSize;

    const targetIndex = Math.max(0, currentIndex - steps);
    return ORDERED_SIZES[targetIndex];
};

/**
 * Calculates an increased frame size based on a step index offset.
 */
const upgradeFrameSize = (currentSize: FrameSize, steps: number): FrameSize => {
    const currentIndex = ORDERED_SIZES.indexOf(currentSize);
    if (currentIndex === -1) return currentSize;

    const targetIndex = Math.min(
        ORDERED_SIZES.length - 1,
        currentIndex + steps,
    );
    return ORDERED_SIZES[targetIndex];
};

/**
 * Evaluates hardware performance profiles and GraphQL configurations
 * to establish optimal runtime thresholds before booting the engine.
 */
export const evaluateEngineInitializationBounds = (
    mode: AnimationModeConfig,
    profile?: DeviceProfile,
) => {
    // Standard baseline configuration fallbacks
    let frameSize: FrameSize = mode.defaultSize;
    let initialFps: number = mode.defaultFps;
    let frameSkip: boolean = false;

    if (!profile) {
        return { initialFps, frameSize, frameSkip };
    }

    // --- 1. NETWORK & MEMORY DOWNGRADING PIPELINE ---
    // Tier 3: Critically constrained profiles drop down 2 full size scales
    if (profile.networkSpeed === 3 || profile.deviceMemory === 3) {
        frameSize = downgradeFrameSize(frameSize, 2);
    }
    // Tier 2: Standard mobile profiles drop down 1 size scale
    else if (profile.networkSpeed === 2 || profile.deviceMemory === 2) {
        frameSize = downgradeFrameSize(frameSize, 1);
    }

    // --- 2. CPU PROCESSING & HARDWARE CONCURRENCY THROTTLING ---
    // Only skip frames if explicit battery saver is active or critical network tier
    if (
        profile.deviceSpeed === 3 &&
        (profile.lowBattery || profile.networkSpeed === 3)
    ) {
        frameSkip = true;
        initialFps = Math.min(
            15,
            Math.max(mode.minFps, Math.floor(initialFps / 2)),
        );
    }

    return {
        initialFps,
        frameSize,
        frameSkip,
    };
};

/**
 * Generates the 3 intent-driven quality options for regular visitors
 * based on device metrics, layout rules, and devicePixelRatio.
 */
export const calculateSmartUserQualityOptions = (
    mode: AnimationModeConfig,
    profile?: DeviceProfile,
) => {
    // 1. Calculate 'AUTO' using your existing network pipeline rules
    const engineSetup = evaluateEngineInitializationBounds(mode, profile);
    const autoSize: FrameSize = engineSetup.frameSize;

    // 2. Calculate 'BEST' using the new devicePixelRatio metric
    const dpr = profile?.devicePixelRatio || 1;
    let bestSize: FrameSize = autoSize;

    if (dpr >= 3) {
        bestSize = upgradeFrameSize(autoSize, 2);
    } else if (dpr >= 1.5) {
        bestSize = upgradeFrameSize(autoSize, 1);
    }

    // 3. Calculate 'LOW' using your logical layout fallbacks
    let lowSize: FrameSize = downgradeFrameSize(autoSize, 1);

    // Fallback checking: Slow connections or slow CPUs force strict lowest 'xxs' baseline
    if (profile && (profile.networkSpeed === 3 || profile.deviceSpeed === 3)) {
        lowSize = 'xxs';
    }

    // If auto is already at the absolute minimum 'xxs', low maps identically to it
    if (autoSize === 'xxs') {
        lowSize = 'xxs';
    }

    return [
        { value: 'low', id: lowSize, label: 'Niedrig' },
        { value: 'auto', id: autoSize, label: 'Auto' },
        { value: 'best', id: bestSize, label: 'Beste Qualität' },
    ];
};
